import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, InputGroup } from 'react-bootstrap';
import {
  Editable,
  fetchApi,
  makeResetCache,
  notifyError,
  notifyResponse,
  PageTitle,
  Section,
  useCacheRecord,
} from 'gossamer-front-end';
import {
  AdminEmailAddressChangeRequest,
  AdminEmailAddressMatch,
  Event,
  EventCoHost,
  InstitutionGroupMember,
  InstitutionMember,
  InstitutionMemberInvite,
  UserView,
} from 'cb-model';
import { Field, FieldType, getRowLabel, QueryOptions, validateEmailAddress } from 'gossamer-unified';

const emailAddressField: Field = { seq: 0, type: FieldType.Text, label: 'Email address' };

// the members search page, on the member or admin invites tab, filtered on the email address
const getInviteSearchPath = (emailAddress: string, isAdmin: boolean) => {
  const queryOptions: QueryOptions = {
    where: {
      and: [
        { fieldId: 'institution_id', operator: 'eq', value: '', display: 'value-edit' },
        { fieldId: 'invitee_email', operator: 'ct', value: emailAddress },
        { fieldId: 'is_admin', operator: 'eq', value: isAdmin, display: 'hidden' },
      ],
    },
    order: [['created_at', 'DESC']],
  };
  const params = new URLSearchParams({
    tab: isAdmin ? 'admin-invites' : 'member-invites',
    q: JSON.stringify(queryOptions),
  });
  return `/admin/institution-member/search?${params.toString()}`;
};

const UserMatch = (props: { match: AdminEmailAddressMatch }) => {
  const record = useCacheRecord(UserView, props.match.key);
  const label = record.data ? getRowLabel(UserView, 'Static', record.data) : props.match.key;
  return (
    <>
      User: <Link to={`/admin/user/display/${props.match.key}`}>{label}</Link>
    </>
  );
};

const EventCoHostMatch = (props: { match: AdminEmailAddressMatch }) => {
  const record = useCacheRecord(Event, props.match.event_id);
  const label = record.data ? getRowLabel(Event, 'Static', record.data) : String(props.match.event_id);
  return (
    <>
      Co-host of event: <Link to={`/admin/event/display/${props.match.event_id}`}>{label}</Link>
    </>
  );
};

const InviteMatch = (props: { match: AdminEmailAddressMatch; emailAddress: string }) => (
  <>
    {props.match.is_admin ? 'Admin' : 'Member'} invite:{' '}
    <Link to={getInviteSearchPath(props.emailAddress, !!props.match.is_admin)}>{props.match.key}</Link>
  </>
);

export const EmailAddressFindAndFix = (): React.JSX.Element => {
  const [emailAddress, setEmailAddress] = React.useState<string | null>(null);
  // the address the matches were found for, so that they aren't confused with a since-edited email address
  const [foundEmailAddress, setFoundEmailAddress] = React.useState<string | null>(null);
  const [matches, setMatches] = React.useState<AdminEmailAddressMatch[] | null>(null);
  const [newEmailAddress, setNewEmailAddress] = React.useState<string | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);

  const canChange = !!matches?.some((match) => match.entityId !== 'User');
  const isNewEmailAddressValid = !!newEmailAddress?.trim() && validateEmailAddress(newEmailAddress) === null;

  const find = async (address: string | null) => {
    if (!address?.trim()) {
      return;
    }
    setIsBusy(true);
    try {
      const response = (await fetchApi(
        'GET',
        `/main/email-address-find?${new URLSearchParams({ emailAddress: address }).toString()}`,
      )) as AdminEmailAddressMatch[];
      setMatches(response);
      setFoundEmailAddress(address);
    } catch (e) {
      notifyError(e, 'An error occurred while finding the email address');
    } finally {
      setIsBusy(false);
    }
  };

  const change = async () => {
    if (!foundEmailAddress || !newEmailAddress) {
      return;
    }
    setIsBusy(true);
    try {
      const payload: AdminEmailAddressChangeRequest = { emailAddress: foundEmailAddress, newEmailAddress };
      const response = await fetchApi('POST', '/main/email-address-change', payload);
      notifyResponse(response);
      resetCache(InstitutionMemberInvite.id);
      resetCache(EventCoHost.id);
      // applying an invite to an existing user changes their memberships
      resetCache(InstitutionMember.id);
      resetCache(InstitutionGroupMember.id);
      // show the records under their new email address
      const changedTo = newEmailAddress.trim();
      setEmailAddress(changedTo);
      setNewEmailAddress(null);
      await find(changedTo);
    } catch (e) {
      notifyError(e, 'An error occurred while changing the email address');
    } finally {
      setIsBusy(false);
    }
  };

  const handleEmailAddressChange = (value: string | null) => {
    setEmailAddress(value);
    if (value !== foundEmailAddress) {
      setMatches(null);
      setFoundEmailAddress(null);
    }
  };

  return (
    <>
      <PageTitle
        title="Email Address Find and Fix"
        subtitle="Find the users, member invites and event co-hosts with an email address, and correct it"
      />
      <Section>
        <InputGroup className="mb-3">
          <InputGroup.Text>Email Address</InputGroup.Text>
          <Editable
            handleChange={handleEmailAddressChange}
            handleBlur={handleEmailAddressChange}
            value={emailAddress}
            field={emailAddressField}
          />
          <Button
            size="sm"
            variant="primary"
            disabled={!emailAddress?.trim() || isBusy}
            onClick={() => find(emailAddress)}
          >
            Find
          </Button>
        </InputGroup>

        {matches && matches.length === 0 && <p>No users, member invites or event co-hosts have this email address.</p>}
        {matches && matches.length > 0 && (
          <ul>
            {matches.map((match) => (
              <li key={`${match.entityId}:${match.key}`}>
                {match.entityId === 'User' && <UserMatch match={match} />}
                {match.entityId === 'InstitutionMemberInvite' && (
                  <InviteMatch match={match} emailAddress={foundEmailAddress?.trim() || ''} />
                )}
                {match.entityId === 'EventCoHost' && <EventCoHostMatch match={match} />}
              </li>
            ))}
          </ul>
        )}

        {matches && matches.length > 0 && (
          <>
            <InputGroup>
              <InputGroup.Text>New Email Address</InputGroup.Text>
              <Editable
                disabled={!canChange}
                handleChange={setNewEmailAddress}
                handleBlur={setNewEmailAddress}
                value={newEmailAddress}
                field={emailAddressField}
              />
              <Button
                size="sm"
                variant="danger"
                disabled={!canChange || !isNewEmailAddressValid || isBusy}
                onClick={change}
              >
                Change
              </Button>
            </InputGroup>
            <p className="form-text">
              {canChange
                ? 'Changes the email address of the member invites and event co-hosts above. Change a user’s email address on their User page.'
                : 'Only users have this email address - change it on their User page.'}
            </p>
          </>
        )}
      </Section>
    </>
  );
};
